import React, { useCallback, useDeferredValue, useMemo, useState } from "react";
import { Redirect } from "react-router-dom";
import TeamScheduleDisplay from "./TeamScheduleDisplay";
import TeamOffensiveStatsDisplay from "./TeamOffensiveStatsDisplay";
import LeagueStatsDisplay from "./LeagueStatsDisplay";
import NFLToolLayout, { SeasonSelect } from "../__shared/NFLToolLayout";
import FilterBar, { FilterLayoutContext } from "../__shared/FilterBar";
import TeamFilter from "../__shared/TeamFilter";
import ToolStatePanel, { TableSkeleton } from "../__shared/ToolStatePanel";
import ToolErrorBoundary from "../__shared/ToolErrorBoundary";
import useNflReference from "../__shared/useNflReference";
import useUrlFilters from "../__shared/useUrlFilters";
import useDebouncedValue from "../__shared/useDebouncedValue";
import { seasonFor } from "../__shared/nflToolFilters";
import { isToolSub } from "../../../../../AuthUtils";
import "./NFLScoutingReportPage.css";

const FILTER_OPTIONS = { positions: [] };
const TITLE = "Scouting Report";
const SUBTITLE =
  "Week-by-week offense and defense for one team, compared with the league.";
const SCHEDULE_COLUMNS = ["Wk", "Tm", "Score", "Opp", "Opp score"];
const LEAGUE_COLUMNS = [
  "Team",
  "Plays",
  "Yards Per Play",
  "Pass Yards Per Att",
  "Rush Yards Per Att",
  "Opp Yards Per Play",
  "Opp Pass Yards Per Att",
  "Opp Rush Yards Per Att",
];

export default function NFLScoutingReportPage() {
  const [referenceFetchKey, setReferenceFetchKey] = useState(0);

  return (
    <ToolErrorBoundary
      what="Scouting report"
      onRetry={() => setReferenceFetchKey((key) => key + 1)}
      wrapError={(panel) => (
        <NFLToolLayout tool="scouting-report" title={TITLE} subtitle={SUBTITLE}>
          {panel}
        </NFLToolLayout>
      )}
    >
      <React.Suspense
        fallback={
          <NFLToolLayout tool="scouting-report" title={TITLE} subtitle={SUBTITLE}>
            <TableSkeleton columns={LEAGUE_COLUMNS} />
          </NFLToolLayout>
        }
      >
        <ScoutingTool referenceFetchKey={referenceFetchKey} />
      </React.Suspense>
    </ToolErrorBoundary>
  );
}

function ChartSkeleton() {
  return (
    <div
      className="nfl-sr-chart-skeleton"
      role="status"
      aria-busy="true"
      aria-label="Loading charts"
    >
      {[0, 1, 2].map((block) => (
        <span key={block} className="nfl-shimmer nfl-sr-chart-shimmer" />
      ))}
    </div>
  );
}

function ScoutingTool({ referenceFetchKey }) {
  const reference = useNflReference({ fetchKey: referenceFetchKey });
  const filters = useUrlFilters(FILTER_OPTIONS);
  const [fetchKey, setFetchKey] = useState(0);

  const season = seasonFor(filters.season, reference);

  const teamByAbbrev = useMemo(() => {
    const map = {};
    reference.teams.forEach((team) => {
      map[team.abbreviation] = team;
    });
    return map;
  }, [reference.teams]);

  const selectedAbbrev = filters.teams.find((abbrev) => teamByAbbrev[abbrev]) || null;
  const team = selectedAbbrev ? teamByAbbrev[selectedAbbrev] : null;

  const combinedKey = `${season ? season.id : ""}|${team ? team.id : ""}`;
  const debouncedKey = useDebouncedValue(combinedKey, 400);

  const variables = useMemo(() => {
    const pipeIndex = debouncedKey.indexOf("|");
    const seasonID = debouncedKey.slice(0, pipeIndex);
    const teamId = debouncedKey.slice(pipeIndex + 1);
    return { seasonID: seasonID || null, teamId: teamId || null };
  }, [debouncedKey]);

  const deferredVariables = useDeferredValue(variables);
  const isUpdating =
    combinedKey !== debouncedKey || variables !== deferredVariables;

  const retry = useCallback(() => setFetchKey((key) => key + 1), []);

  const selectTeam = useCallback(
    (abbrev) => filters.setTeams(selectedAbbrev === abbrev ? [] : [abbrev]),
    [filters, selectedAbbrev]
  );

  if (!isToolSub(reference.groups)) {
    return <Redirect to="/memberships" />;
  }

  const teamFilter = (
    <TeamFilter
      label="Team"
      teams={reference.teams}
      selected={selectedAbbrev ? [selectedAbbrev] : []}
      onToggle={selectTeam}
    />
  );

  const ready = Boolean(deferredVariables.teamId);

  const chips = team
    ? [
        {
          key: `team-${team.abbreviation}`,
          label: team.name,
          color: team.primaryColorHex,
          onRemove: () => selectTeam(team.abbreviation),
        },
      ]
    : [];

  const seasonSelect = (
    <SeasonSelect
      seasons={reference.seasons}
      value={season ? season.year : ""}
      onChange={(year) =>
        filters.setSeason(
          reference.currentSeason && year === reference.currentSeason.year
            ? null
            : year
        )
      }
    />
  );

  if (!team) {
    return (
      <NFLToolLayout
        tool="scouting-report"
        title={TITLE}
        subtitle={SUBTITLE}
        actions={seasonSelect}
      >
        <ToolStatePanel
          title="Pick a team"
          body="Choose a team to see its week-by-week offense and defense."
        >
          <div className="nfl-sr-pick">
            <FilterLayoutContext.Provider value="sheet">
              {teamFilter}
            </FilterLayoutContext.Provider>
          </div>
        </ToolStatePanel>
      </NFLToolLayout>
    );
  }

  return (
    <NFLToolLayout
      tool="scouting-report"
      title={TITLE}
      subtitle={SUBTITLE}
      accentColor={team.primaryColorHex}
      actions={seasonSelect}
      filters={
        <FilterBar
          primary={teamFilter}
          chips={chips}
          onClearAll={() => filters.clear("teams")}
          isUpdating={isUpdating}
        />
      }
    >
      <div className={isUpdating ? "nfl-is-updating" : undefined}>
        <div className="nfl-sr-layout">
          <div className="nfl-sr-rail">
            <ToolErrorBoundary what="Schedule" onRetry={retry}>
              <React.Suspense
                fallback={<TableSkeleton columns={SCHEDULE_COLUMNS} rows={6} />}
              >
                {ready ? (
                  <TeamScheduleDisplay
                    teamId={deferredVariables.teamId}
                    seasonID={deferredVariables.seasonID}
                    fetchKey={fetchKey}
                  />
                ) : (
                  <TableSkeleton columns={SCHEDULE_COLUMNS} rows={6} />
                )}
              </React.Suspense>
            </ToolErrorBoundary>
          </div>

          <div className="nfl-sr-main">
            <ToolErrorBoundary what="Team stats" onRetry={retry}>
              <React.Suspense fallback={<ChartSkeleton />}>
                {ready ? (
                  <TeamOffensiveStatsDisplay
                    teamId={deferredVariables.teamId}
                    seasonID={deferredVariables.seasonID}
                    fetchKey={fetchKey}
                  />
                ) : (
                  <ChartSkeleton />
                )}
              </React.Suspense>
            </ToolErrorBoundary>
          </div>
        </div>

        <section className="nfl-sr-league">
          <h2 className="nfl-sr-league-title nfl-display">League stats</h2>
          <ToolErrorBoundary what="League stats" onRetry={retry}>
            <React.Suspense fallback={<TableSkeleton columns={LEAGUE_COLUMNS} />}>
              {ready ? (
                <LeagueStatsDisplay
                  teamId={deferredVariables.teamId}
                  seasonID={deferredVariables.seasonID}
                  fetchKey={fetchKey}
                />
              ) : (
                <TableSkeleton columns={LEAGUE_COLUMNS} />
              )}
            </React.Suspense>
          </ToolErrorBoundary>
        </section>
      </div>
    </NFLToolLayout>
  );
}
